import type {
  CancellationToken,
  editor,
  IPosition,
  languages,
} from 'monaco-editor'

import {
  type LanguageWorkerAccessor,
  nullSafeTextMapper,
  type WorkerMapping,
} from '../worker/api'

export class FPlusSignatureHelpProvider<T>
  implements languages.SignatureHelpProvider
{
  signatureHelpTriggerCharacters: readonly string[] = ['(', ',']
  signatureHelpRetriggerCharacters: readonly string[] = [' ']
  private worker: LanguageWorkerAccessor<T>
  private asWorkerInput: WorkerMapping<T>

  constructor(
    worker: LanguageWorkerAccessor<T>,
    asWorkerInput: WorkerMapping<T>,
  ) {
    this.worker = worker
    this.asWorkerInput = asWorkerInput
  }

  provideSignatureHelp(
    model: editor.ITextModel,
    position: IPosition,
    _token: CancellationToken,
    context: languages.SignatureHelpContext,
  ): languages.ProviderResult<languages.SignatureHelpResult> {
    return this.signatureHelp(model, position, context)
  }

  private async signatureHelp(
    model: editor.ITextModel,
    position: IPosition,
    context: languages.SignatureHelpContext,
  ): Promise<languages.SignatureHelpResult | null> {
    const worker = await this.worker(model.uri)

    if (!worker) {
      return null
    }

    const input = await this.asWorkerInput({ model, position })

    if (!input?.data) {
      return null
    }

    const signatureHelp = await worker.signatureHelp(input.data)

    if (context.activeSignatureHelp && !signatureHelp.items) {
      return this.asResult(context.activeSignatureHelp)
    }

    const textMapper = nullSafeTextMapper(input)

    return this.asResult({
      activeParameter: signatureHelp?.activeParameter ?? 0,
      activeSignature: signatureHelp?.activeSignature ?? 0,
      signatures: signatureHelp.items.map((i) => ({
        label: i.label ?? '',
        documentation: {
          value: textMapper(i.documentation) ?? '',
          isTrusted: false,
          supportThemeIcons: true,
          supportHtml: false,
        },
        activeParameter: i.activeParameter ?? 0,
        parameters: (i.parameters ?? []).map((p) => ({
          label: p.label ?? '',
        })),
      })),
    })
  }

  private asResult(
    value: languages.SignatureHelp,
  ): languages.SignatureHelpResult {
    return {
      value,
      dispose: () => undefined,
    }
  }
}
